import { useState, useRef, useCallback, useMemo } from 'react'

type AttachmentUploadState = {
  uploadingFileIds: Map<string, File>
  failedUploads: Map<string, File>
  hasFailedUploads: boolean
}

type AttachmentUploadActions = {
  addUploadingFiles: (files: Map<string, File>) => void
  removeUploadingFile: (id: string) => void
  markAsFailed: (id: string, file: File) => void
  markAllAsFailed: (files: Map<string, File>) => void
  removeFailed: (id: string) => void
  clearFailed: () => void
  clearAll: () => void
}

export const useAttachmentUploadState = () => {
  const [uploadingFileIds, setUploadingFileIds] = useState<Map<string, File>>(new Map())
  const failedUploadsRef = useRef<Map<string, File>>(new Map())
  const [hasFailedUploads, setHasFailedUploads] = useState(false)

  const addUploadingFiles = useCallback((files: Map<string, File>) => {
    setUploadingFileIds(prev => new Map([...prev, ...files]))
  }, [])

  const removeUploadingFile = useCallback((id: string) => {
    setUploadingFileIds(prev => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
    failedUploadsRef.current.delete(id)
  }, [])

  const markAsFailed = useCallback((id: string, file: File) => {
    failedUploadsRef.current.set(id, file)
    setHasFailedUploads(true)
  }, [])

  const markAllAsFailed = useCallback((files: Map<string, File>) => {
    files.forEach((file, id) => {
      failedUploadsRef.current.set(id, file)
    })
    setHasFailedUploads(failedUploadsRef.current.size > 0)
  }, [])

  const removeFailed = useCallback((id: string) => {
    failedUploadsRef.current.delete(id)
    setHasFailedUploads(failedUploadsRef.current.size > 0)
  }, [])

  const clearFailed = useCallback(() => {
    failedUploadsRef.current.clear()
    setHasFailedUploads(false)
  }, [])

  const clearAll = useCallback(() => {
    setUploadingFileIds(new Map())
    failedUploadsRef.current.clear()
    setHasFailedUploads(false)
  }, [])

  const state: AttachmentUploadState = useMemo(
    () => ({
      uploadingFileIds,
      failedUploads: failedUploadsRef.current,
      hasFailedUploads,
    }),
    [uploadingFileIds, hasFailedUploads],
  )

  const actions: AttachmentUploadActions = useMemo(
    () => ({
      addUploadingFiles,
      removeUploadingFile,
      markAsFailed,
      markAllAsFailed,
      removeFailed,
      clearFailed,
      clearAll,
    }),
    [addUploadingFiles, removeUploadingFile, markAsFailed, markAllAsFailed, removeFailed, clearFailed, clearAll],
  )

  return { state, actions, failedUploadsRef }
}

